import React, {
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
  useMemo,
} from 'react';
import { useQuery } from '@apollo/client';
import { IFormBag } from '../../../../Types/FormTypes';
import { LoginStatus, LoginValues } from '../../Login';
import EDKeybind from '../../../../BaseComponents/Keybinds/EDKeybind';
import EDPaper from '../../../../BaseComponents/Paper/EDPaper';
import Field from '../../../../BaseComponents/Forms/EDField';
import InfoArea from '../InfoArea/InfoArea';
import EDButton from '../../../../BaseComponents/Buttons/Button/EDButton';
import EDCheckbox from '../../../../BaseComponents/Checkboxes/EDCheckbox';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import avenyLocalStorage from '../../../../Utils/LocalStorage/avenyLocalStorage';
import {
  LOCAL_STORAGE_LOGIN_USERNAME_KEY,
  LOCAL_STORAGE_LOGIN_DATABASE_KEY,
  LOCAL_STORAGE_LOGIN_SYSTEM_KEY,
  LOCAL_STORAGE_LOGIN_REMEMBER_ME_KEY,
} from '../../../../Utils/LocalStorage/localStorageKeys';
import Logger, { Environment } from '../../../../Utils/Logger';
import localDatabasesQuery from '../../Queries/LocalDatabasesQuery.gql';
import {
  LocalDatabasesQuery,
  LocalDatabasesQueryVariables,
  LocalDatabasesQuery_databases,
} from '../../Queries/_QueryTypes/LocalDatabasesQuery';
import EniacLogo from '../../../../Assets/Logos/Logo_RGB_trans.svg';
import { EDAutoCompleteEdit } from '../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { System } from '../../../Routing/Utils/routerUtils';
import { pathJoin } from '../../../../Utils/stringUtils';
import { useLocation } from 'react-router';
import { toString, uniqBy } from 'ramda';
import { IConsent } from '../../../../Components/CookieSettings/CookieSettingsUtils';
import { getServerURL } from '../../../../Utils/getServerURL';
import { bankIDMessages } from './bankIDMessages';
import isMobileLib from 'is-mobile';
import EDLink from '../../../../BaseComponents/Link/EDLink';
import { EDQRCode } from '../../../../BaseComponents/QRCode/EDQRCode';
import { useApolloClient } from '@apollo/client';
import databasesQuery from '../../../../Components/QueryComponents/DatabasesQuery/DatabasesQuery.gql';
import { DatabasesQuery as IDatabasesQueryData } from '../../../../Components/QueryComponents/DatabasesQuery/_QueryTypes/DatabasesQuery';
import { EDTextEdit } from '../../../../BaseComponents/TextEdit/EDTextEdit';
import ResetPwdLink from './Components/ResetPwdLink/ResetPwdLink';
import EDBox from '../../../../BaseComponents/EDBox/EDBox';
import { stringCompare } from '../../../../Utils/mergeSort';
import {
  DatabaseInfoQuery,
  DatabaseInfoQueryVariables,
} from '../../Queries/_QueryTypes/DatabaseInfoQuery';
import databaseInfoQuery from '../../Queries/DatabaseInfoQuery.gql';
import EDTypography from '../../../../BaseComponents/Typography/EDTypography';

interface IProps {
  consent: IConsent;
  setConsent: (consent: IConsent) => void;
  formProps: IFormBag;
  handleEnterKeyUp: () => void;
  loginMessage: string;
  rememberMe: boolean;
  handleRememberMeChange: (value: boolean) => void;
  loginStatus: LoginStatus;
  setLoginStatus: (value: LoginStatus, callback?: () => void) => void;
  onAbortLogin: () => void;
  setLoginMessage: (message: string) => void;
  classes?: any;
}

const styles = (theme: Theme) => ({
  paper: {
    boxSizing: 'border-box',
    margin: `${theme.spacing(3)} 0 0 0`,
    [theme.breakpoints.up('sm')]: {
      width: '320px',
    },
  },
  iconLabel: {
    display: 'inline-block',
    height: '24px',
    verticalAlign: 'middle',
    marginLeft: '10px',
  },
  dialogTitle: {
    margin: '4px 0',
    height: '52px',
  },
  optionsContainer: {
    margin: '16px 0',
  },
  cookiePolicyLinkContainer: {
    textAlign: 'right',
  },
  linkContainer: {
    margin: '8px 0',
    display: 'flex',
    justifyContent: 'space-between',
  },
  logoContainer: {
    textAlign: 'center',
    margin: '24px 0',
  },
  logo: {
    width: '120px',
  },
});
export type BankIDStatus =
  | 'NO_LOGIN_IN_PROGRESS'
  | 'BANKID_COMPLETE'
  | 'BANKID_ERROR'
  | 'LOGIN_STARTED'
  | 'BANKID_STARTING'
  | 'BANKID_STARTING_MOBILE'
  | 'BANKID_SHOW_QR';

interface IBankIDState {
  status: BankIDStatus;
  autostartToken?: string;
  qrCode?: string;
  id?: string;
  errorMessage?: string;
}

/** Ingen pågående login */
const bankIDNoLogin: IBankIDState = {
  status: 'NO_LOGIN_IN_PROGRESS',
};

/** Inloggning påbörjad, ingen respons än */
const bankIDLoginStarted: IBankIDState = {
  status: 'LOGIN_STARTED',
};

const bankIdStatusMessage = (bankIDState: IBankIDState): string => {
  switch (bankIDState.status) {
    case 'NO_LOGIN_IN_PROGRESS':
      return '';
    case 'LOGIN_STARTED':
      return 'Inloggning via BankID påbörjad';
    case 'BANKID_STARTING':
    case 'BANKID_STARTING_MOBILE':
      return bankIDMessages.RFA13;
    case 'BANKID_SHOW_QR':
      return bankIDMessages.hintcodeQrstarted;
    case 'BANKID_COMPLETE':
      return 'Inloggning via BankID klar';
    case 'BANKID_ERROR':
      return 'Fel vid inloggning:' + bankIDState.errorMessage;
    default:
      return 'okänd status: ' + bankIDState;
  }
};
const noDatabases: LocalDatabasesQuery_databases[] = [];

const isIOS = (): boolean =>
  ['IPad', 'IPhone', 'IPod'].includes(window.navigator.platform);
/**
 * Vilken enhet vi kör på för BankId inloggningen
 * Separat från annan hantering av mobilanpassning för
 * att vi här är intresserade av vilket operativsystem och vilken brower som körs men vi
 * annars är intresserade av features som skärmstorlek eller om det är en touch device
 */
const useDevice = () => {
  const isMobileOrTablet = isMobileLib({ tablet: true, featureDetect: true });
  const isChromeOnAppleDevice = Boolean(navigator.userAgent.match(/CriOS/));
  const isFirefoxOnAppleDevice = Boolean(navigator.userAgent.match(/FxiOS/));
  const isOperaTouchOnAppleDevice = Boolean(navigator.userAgent.match(/OPT/));

  return {
    isMobileOrTablet,
    isChromeOnAppleDevice,
    isFirefoxOnAppleDevice,
    isOperaTouchOnAppleDevice,
  };
};

const LoginForm: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { setLoginStatus, formProps, setLoginMessage } = props;
  const apolloClient = useApolloClient();
  const { query, reFetchObservableQueries } = apolloClient;
  const location = useLocation();
  const device = useDevice();
  const formValues = props.formProps.values as LoginValues;
  const [loadingLoginSettings, setLoadingLoginSettings] = useState(true);
  const [usernameInputFocused, setUsernameInputFocused] = useState(false);
  const [bankIDStatus, setBankIDStatus] = useState<IBankIDState>(bankIDNoLogin);

  const shouldSkipNetworkRequest1 =
    formValues.system === System.GAS &&
    (!formValues.username || usernameInputFocused);

  const {
    data: localDatabasesQueryData1,
    loading: localDatabasesQueryLoading1,
    error: localDatabasesQueryError1,
  } = useQuery<LocalDatabasesQuery, LocalDatabasesQueryVariables>(
    localDatabasesQuery,
    {
      variables: {
        sign:
          formValues.system === System.GAS && formValues.username
            ? formValues.username
            : '',
        gbs: true,
      },
      fetchPolicy: shouldSkipNetworkRequest1 ? 'cache-only' : 'cache-first',
    }
  );

  const shouldSkipNetworkRequest2 =
    formValues.system !== System.GAS ||
    !formValues.username ||
    usernameInputFocused;

  const {
    data: localDatabasesQueryData2,
    loading: localDatabasesQueryLoading2,
    error: localDatabasesQueryError2,
  } = useQuery<LocalDatabasesQuery, LocalDatabasesQueryVariables>(
    localDatabasesQuery,
    {
      variables: {
        sign: formValues.username,
        avenyID: true,
      },
      fetchPolicy: shouldSkipNetworkRequest2 ? 'cache-only' : 'cache-first',
    }
  );

  const databasesListLoading = (() => {
    if (formValues.system === System.Lokalt) {
      return localDatabasesQueryLoading1;
    }

    const q1CompletedHasDb =
      !localDatabasesQueryLoading1 &&
      !!localDatabasesQueryData1?.databases?.length;
    const q2CompletedHasDb =
      !localDatabasesQueryLoading2 &&
      !!localDatabasesQueryData2?.databases?.length;
    const q1CompletedNoDb = !localDatabasesQueryLoading1 && !q1CompletedHasDb;
    const q2CompletedNoDb = !localDatabasesQueryLoading2 && !q2CompletedHasDb;

    // If any completed with databases => false
    if (q1CompletedHasDb || q2CompletedHasDb) return false;
    // If both completed (regardless of empty) => false
    if (!localDatabasesQueryLoading1 && !localDatabasesQueryLoading2)
      return false;
    // If one loading and the other completed with no databases => true
    if (
      (localDatabasesQueryLoading1 && q2CompletedNoDb) ||
      (localDatabasesQueryLoading2 && q1CompletedNoDb)
    )
      return true;
    // If both loading => true
    if (localDatabasesQueryLoading1 && localDatabasesQueryLoading2) return true;
    // Fallback
    return false;
  })();

  const databases1 =
    !localDatabasesQueryLoading1 &&
    !localDatabasesQueryError1 &&
    localDatabasesQueryData1 &&
    localDatabasesQueryData1.databases
      ? localDatabasesQueryData1.databases
      : noDatabases;
  const databases2 =
    !localDatabasesQueryLoading2 &&
    !localDatabasesQueryError2 &&
    localDatabasesQueryData2 &&
    localDatabasesQueryData2.databases
      ? localDatabasesQueryData2.databases
      : noDatabases;
  const databases = useMemo(
    () =>
      uniqBy(ds => ds.name, [...databases1, ...databases2]).sort((ds1, ds2) =>
        stringCompare(ds1.name, ds2.name)
      ),
    [databases1, databases2]
  );
  const databaseNames = useMemo(() => {
    return databases.map(db => ({
      name: `${db.name}${db.avenyidBehorighet ? ' (AvenyID)' : ''}`,
      value: db.name,
    }));
  }, [databases]);
  const avenyIdDatabaseNames = useMemo(() => {
    return databases.filter(db => db.avenyidBehorighet).map(db => db.name);
  }, [databases]);

  const { data: databaseInfoData, loading: databaseInfoLoading } = useQuery<
    DatabaseInfoQuery,
    DatabaseInfoQueryVariables
  >(databaseInfoQuery, {
    variables: {
      dbName: formValues.database,
    },
    skip:
      !formValues.database ||
      Boolean(databases.find(db => db.name === formValues.database)),
  });

  const currentDatabase =
    databases.find(db => db.name === formValues.database) ??
    databaseInfoData?.databaseInfo;

  useEffect(() => {
    const databaseUrl = currentDatabase?.testkundURL;
    const canRedirect =
      typeof window.env.canRedirect === 'boolean'
        ? window.env.canRedirect
        : typeof window.env.canRedirect === 'string' &&
          databaseUrl?.startsWith(window.env.canRedirect)
        ? true
        : window.env.environment === Environment.production;
    const currentLocation = window.location.href;
    if (
      canRedirect &&
      databaseUrl &&
      !currentLocation.startsWith(databaseUrl)
    ) {
      window.location.href = pathJoin([databaseUrl, '#', location.pathname]);
    }
  }, [currentDatabase, location.pathname]);

  //Hantera om användaren valt "Kom ihåg mig"
  useEffect(() => {
    if (props.loginStatus === 'NO_LOGIN_IN_PROGRESS') {
      if (formValues.database) {
        setLoadingLoginSettings(false);
      } else {
        const userNamePromise = avenyLocalStorage.getItem<string>(
          LOCAL_STORAGE_LOGIN_USERNAME_KEY
        );
        const databasePromise = avenyLocalStorage.getItem<string>(
          LOCAL_STORAGE_LOGIN_DATABASE_KEY
        );
        const systemPromise = avenyLocalStorage.getItem<string>(
          LOCAL_STORAGE_LOGIN_SYSTEM_KEY
        );
        const rememberMePromise = avenyLocalStorage.getItem<boolean>(
          LOCAL_STORAGE_LOGIN_REMEMBER_ME_KEY
        );

        Promise.all([
          userNamePromise,
          databasePromise,
          systemPromise,
          rememberMePromise,
        ])
          .then(values => {
            if (values[3]) {
              props.formProps.setValues({
                ...props.formProps.values,
                username: values[0] || '',
                database: values[1] || '',
                system: values[2] || System.Lokalt,
                password: '',
              });
            }
            props.handleRememberMeChange(Boolean(values[3]));
          })
          .catch(error => {
            Logger.error(
              Error(
                'Failed to load loginsettings from localstorage: ' +
                  JSON.stringify(error, null, 2)
              )
            );
          })
          .finally(() => setLoadingLoginSettings(false));
      }
    } else {
      // Om vi inte startar i NO_LOGIN_IN_PROGRESS så läser vi inte in login settings
      setLoadingLoginSettings(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginClick = useCallback(() => {
    setLoginStatus('LOCAL_LOGIN_STARTED', formProps.submitForm);
  }, [setLoginStatus, formProps.submitForm]);

  const checkAutofilled = () => {
    /**
     * The field can be prefilled on the very first page loading by the browser
     * By the security reason browser limits access to the field value from JS level and the value becomes available
     * only after first user interaction with the page
     */
    const passwordAutofilled = !!document
      .getElementById('password')
      ?.matches('*:-webkit-autofill');
    const usernameAutofilled = !!document
      .getElementById('username')
      ?.matches('*:-webkit-autofill');
    if (passwordAutofilled !== props.formProps.values.passwordAutofilled) {
      props.formProps.setFieldValue('passwordAutofilled', passwordAutofilled);
    }
    if (usernameAutofilled !== props.formProps.values.usernameAutofilled) {
      props.formProps.setFieldValue('usernameAutofilled', usernameAutofilled);
    }
  };

  useLayoutEffect(() => {
    // The time when it's ready is not very stable, so check few times
    setTimeout(checkAutofilled, 500);
    setTimeout(checkAutofilled, 1000);
    setTimeout(checkAutofilled, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Avbryt inloggning med BankId */
  const abortBankIDLogin = () => {
    props.setLoginStatus('NO_LOGIN_IN_PROGRESS');
    setBankIDStatus(bankIDNoLogin);
  };

  /**
   * Påbörja inloggning med BankId
   * Sätter status och anropar bankid-auth
   * @param autostart Starta BankId på enheten
   * @param mobile inloggningen startar från en iOS/android enhet
   */
  const startBankIDLogin = (autostart: boolean, mobile: boolean) => {
    props.setLoginMessage(`loggar in med BankID`);
    props.setLoginStatus('BANKID_LOGIN_STARTED');
    setBankIDStatus(bankIDLoginStarted);

    const loginInfo = {
      db: props.formProps.values.database,
    };
    fetch(`${getServerURL()}/bankid-auth`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        details: loginInfo,
      }),
    })
      .then(response => {
        response.json().then(json => {
          setBankIDStatus({
            status: autostart
              ? mobile
                ? 'BANKID_STARTING_MOBILE'
                : 'BANKID_STARTING'
              : 'BANKID_SHOW_QR',
            autostartToken: json.autoStartToken,
            id: json.id,
          });
          return json;
        });
      })
      .catch((error: Error) => {
        props.setLoginStatus('NO_LOGIN_IN_PROGRESS');
        setBankIDStatus({
          status: 'BANKID_ERROR',
          errorMessage: error.message,
        });
        props.setLoginMessage(error.message);
      });
  };

  //Fråga efter status på inloggningen med 2s intervall om vi försökt starta appen eller visar QR
  useEffect(() => {
    if (
      (bankIDStatus.status === 'BANKID_STARTING' ||
        bankIDStatus.status === 'BANKID_STARTING_MOBILE' ||
        bankIDStatus.status === 'BANKID_SHOW_QR') &&
      bankIDStatus.id
    ) {
      const id = setInterval(() => {
        fetch(`${getServerURL()}/bankid-collect`, {
          method: 'POST',
          credentials: 'include',
          body: JSON.stringify({ id: bankIDStatus.id }),
        })
          .then(response => {
            response.json().then(
              json => {
                if (json.status === 'complete') {
                  setBankIDStatus({
                    status: 'BANKID_COMPLETE',
                  });

                  query<IDatabasesQueryData, {}>({
                    query: databasesQuery,
                  })
                    .then(result => {
                      reFetchObservableQueries();
                    })
                    .finally(() => {
                      setLoginStatus('NO_LOGIN_IN_PROGRESS');
                    });
                } else if (json.status === 'failed' || json.Error) {
                  setLoginStatus('NO_LOGIN_IN_PROGRESS');
                  setBankIDStatus({
                    status: 'BANKID_ERROR',
                    errorMessage: toString(json),
                  });
                  setLoginMessage(toString(json));
                } else {
                  setBankIDStatus(state => ({
                    ...state,
                    qrCode: json.qr,
                  }));
                }
                return json;
              },
              reason => {
                setLoginStatus('NO_LOGIN_IN_PROGRESS');
                setBankIDStatus({
                  status: 'BANKID_ERROR',
                  errorMessage: toString(reason),
                });
                setLoginMessage(toString(reason));
              }
            );
          })
          .catch(error => {
            setLoginStatus('NO_LOGIN_IN_PROGRESS');
            setBankIDStatus({
              status: 'BANKID_ERROR',
              errorMessage: toString(error),
            });
            setLoginMessage(error.message);
          });
      }, 2000);
      return () => clearInterval(id);
    } else {
      return undefined;
    }
  }, [
    query,
    reFetchObservableQueries,
    bankIDStatus,
    setLoginMessage,
    setLoginStatus,
  ]);
  //Försök starta BankID-appen om status sätt till BANKID_STARTING
  useEffect(() => {
    if (bankIDStatus.status === 'BANKID_STARTING') {
      const redirect = 'null';
      const startUrl = `bankid:///?autostarttoken=${bankIDStatus.autostartToken}&redirect=${redirect}`;
      window.location.href = startUrl;
    } else if (bankIDStatus.status === 'BANKID_STARTING_MOBILE') {
      const redirect = isIOS()
        ? encodeURIComponent(
            `${window.location.href}?getResult=${bankIDStatus.id}`
          )
        : 'null';
      const startUrl = `https://app.bankid.com/?autostarttoken=${bankIDStatus.autostartToken}&redirect=${redirect}`;
      window.location.href = startUrl;
    }
  }, [bankIDStatus.status, bankIDStatus.autostartToken, bankIDStatus.id]);

  // Default är att starta på den aktuella enheten om det är android/iOS annars att visa QR
  const startDefaultBankIdLogin = () => {
    if (device.isMobileOrTablet) {
      startBankIDLogin(true, true);
    } else {
      startBankIDLogin(false, false);
    }
  };

  const switchToAutoStart = () => {
    setBankIDStatus(state => ({
      ...state,
      status: device.isMobileOrTablet
        ? 'BANKID_STARTING_MOBILE'
        : 'BANKID_STARTING',
    }));
  };

  const switchToQR = () => {
    setBankIDStatus(state => ({ ...state, status: 'BANKID_SHOW_QR' }));
  };
  //Visa inte formuläret förrän vi kollat upp om vi ska fylla i något, autofocus behöver vara rätt innan mounting
  if (loadingLoginSettings && !window.env.disableLoginSettingsLoadWait) {
    return null;
  }
  return (
    <EDKeybind keymap="Enter" onKeyDown={props.handleEnterKeyUp}>
      <EDPaper className={props.classes.paper} key="loginPage">
        <>
          <Field name="system">
            {({ value, onChange }) => (
              <EDCheckbox
                label="Central inloggning"
                tooltip="Logga in med konto i Gemensamma behörighetssystemet eller med AvenyID"
                checked={value === System.GAS}
                onCheck={checked => {
                  onChange(checked ? System.GAS : System.Lokalt);
                  props.formProps.setFieldValue('database', '');
                }}
                testId="GBS"
                disabled={
                  props.loginStatus != 'NO_LOGIN_IN_PROGRESS' ||
                  databaseInfoLoading
                }
              />
            )}
          </Field>
          <Field name="username">
            {({ value, onChange }) => (
              <EDTextEdit
                id="username"
                autoComplete="username"
                testId="usernameInput"
                onChange={value => {
                  props.formProps.setFieldValue('usernameAutofilled', false);
                  onChange(value);
                }}
                value={value ?? ''}
                label="Användarnamn"
                staticLabel={!!props.formProps.values.usernameAutofilled}
                autoFocus={!value}
                onFocus={() => {
                  setUsernameInputFocused(true);
                }}
                onBlur={() => {
                  setUsernameInputFocused(false);
                }}
                readOnly={
                  props.loginStatus != 'NO_LOGIN_IN_PROGRESS' ||
                  databaseInfoLoading
                }
                fullWidth
              />
            )}
          </Field>
          <Field name="database">
            {({ value, onChange }) => {
              return (
                <EDAutoCompleteEdit<string, undefined, true>
                  label="Sök/Välj Databas"
                  options={databaseNames}
                  suggestionValue
                  value={value}
                  baseValue={value}
                  onChange={(suggestionValue: any) => {
                    onChange(suggestionValue?.value);
                  }}
                  fullWidth
                  testId="databaseAutocomplete"
                  disableOpenOnFocus
                  readOnly={
                    props.loginStatus != 'NO_LOGIN_IN_PROGRESS' ||
                    databaseInfoLoading
                  }
                  loading={databasesListLoading}
                />
              );
            }}
          </Field>
          {!databasesListLoading &&
            localDatabasesQueryError1 &&
            Boolean(formValues.username) &&
            !usernameInputFocused && (
              <EDTypography variant="body2" color="error">
                Fel vid kontroll mot GBS: {localDatabasesQueryError1?.message}
              </EDTypography>
            )}
          {!databasesListLoading &&
            localDatabasesQueryError2 &&
            Boolean(formValues.username) &&
            !usernameInputFocused && (
              <EDTypography variant="body2" color="error">
                Fel vid kontroll av AvenyID:{' '}
                {localDatabasesQueryError2?.message}
              </EDTypography>
            )}
          {!databasesListLoading &&
            !localDatabasesQueryError1 &&
            !localDatabasesQueryError2 &&
            databaseNames.length === 0 &&
            Boolean(formValues.username) &&
            !usernameInputFocused && (
              <EDTypography variant="body2" color="error">
                Behörighet saknas
              </EDTypography>
            )}
          {props.loginStatus === 'BANKID_LOGIN_STARTED' && (
            <>
              <InfoArea type="info">
                {bankIdStatusMessage(bankIDStatus)}
              </InfoArea>
              {bankIDStatus.status === 'BANKID_SHOW_QR' && (
                <EDQRCode qrCode={bankIDStatus.qrCode} width={320} />
              )}
            </>
          )}
          {currentDatabase?.bankID &&
            bankIDStatus.status === 'BANKID_SHOW_QR' && (
              <EDLink
                onClick={switchToAutoStart}
                disabled={
                  !(
                    props.loginStatus === 'NO_LOGIN_IN_PROGRESS' ||
                    props.loginStatus === 'BANKID_LOGIN_STARTED'
                  ) || databaseInfoLoading
                }
                testId="loginBankIDAutoStart"
                buttonComponent
              >
                Logga in med BankID på denna enhet istället
              </EDLink>
            )}
          {currentDatabase?.bankID &&
            bankIDStatus.status === 'BANKID_STARTING' && (
              <EDLink
                onClick={switchToQR}
                disabled={
                  !(
                    props.loginStatus === 'NO_LOGIN_IN_PROGRESS' ||
                    props.loginStatus === 'BANKID_LOGIN_STARTED'
                  ) || databaseInfoLoading
                }
                testId="loginBankIDOtherDevice"
                buttonComponent
              >
                Logga in med BankId på annan enhet istället
              </EDLink>
            )}
          {bankIDStatus.status === 'NO_LOGIN_IN_PROGRESS' && (
            <Field name="password">
              {({ value, onChange }) => (
                <EDTextEdit
                  type="password"
                  id="password"
                  showPasswordToggle
                  autoComplete="current-password"
                  testId="passwordInput"
                  onChange={value => {
                    props.formProps.setFieldValue('passwordAutofilled', false);
                    onChange(value);
                  }}
                  value={value}
                  label="Lösenord"
                  staticLabel={!!props.formProps.values.passwordAutofilled}
                  autoFocus={Boolean(formValues.username)}
                  readOnly={
                    props.loginStatus != 'NO_LOGIN_IN_PROGRESS' ||
                    databaseInfoLoading
                  }
                  fullWidth
                />
              )}
            </Field>
          )}
          <InfoArea type="info" testId="loginMessage">
            {props.loginMessage}
          </InfoArea>
          {bankIDStatus.status === 'NO_LOGIN_IN_PROGRESS' && (
            <EDButton
              label={'Logga in'}
              disabled={
                !props.formProps.isValid ||
                props.loginStatus !== 'NO_LOGIN_IN_PROGRESS' ||
                databaseInfoLoading ||
                !databases.find(db => db.name === formValues.database)
              }
              onClick={loginClick}
              primary
              testId="loginButton"
              fullWidth
              marginY={2}
            />
          )}
          {currentDatabase?.bankID && (
            <EDButton
              label={
                props.loginStatus === 'BANKID_LOGIN_STARTED'
                  ? 'Avbryt inloggning med BankID'
                  : 'Logga in med BankID'
              }
              onClick={
                props.loginStatus === 'BANKID_LOGIN_STARTED'
                  ? abortBankIDLogin
                  : startDefaultBankIdLogin
              }
              disabled={
                !(
                  props.loginStatus === 'NO_LOGIN_IN_PROGRESS' ||
                  props.loginStatus === 'BANKID_LOGIN_STARTED'
                ) || databaseInfoLoading
              }
              primary
              testId="loginBankIDButton"
              fullWidth
              marginY={2}
            />
          )}
          <EDBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <EDCheckbox
              label="Kom ihåg mig!"
              checked={props.rememberMe}
              onCheck={props.handleRememberMeChange}
            />

            <ResetPwdLink
              gas={(formProps.values as LoginValues).system === 'GAS'}
              lokalt={(formProps.values as LoginValues).system === 'Lokalt'}
              avenyId={Boolean(
                avenyIdDatabaseNames &&
                  avenyIdDatabaseNames.find(
                    db => db === (formProps.values as LoginValues).database
                  )
              )}
              dbname={(formProps.values as LoginValues).database}
              email={
                (formProps.values as LoginValues).username &&
                (formProps.values as LoginValues).username.includes('@')
                  ? (formProps.values as LoginValues).username
                  : undefined
              }
            >
              <span>Glömt lösenord?</span>
            </ResetPwdLink>
          </EDBox>
          <div className={props.classes.logoContainer}>
            <img
              src={EniacLogo}
              alt="Eniac logotyp"
              className={props.classes.logo}
            />
          </div>
        </>
      </EDPaper>
    </EDKeybind>
  );
};

export default withStyles(LoginForm, styles);
